import { useCallback, useMemo } from 'react';
import type { DeepLinkUIStateLinkedEntityNodeValue, UseDeepLinkRuntimeStateReturn } from '../../types';
import { isValidLinkedEntityNodeUIStatePath } from '../utils';
import { useDeepLinkContext } from './useDeepLinkContext';

export const useDeepLinkRuntimeState = (): UseDeepLinkRuntimeStateReturn => {
    const deepLinkContext = useDeepLinkContext();

    const getDeepLinkRuntimeState = useCallback<UseDeepLinkRuntimeStateReturn['getDeepLinkRuntimeState']>(
        <T = Record<string, unknown>>(path: string) => {
            return deepLinkContext.getDeepLinkRuntimeState<T>({ path });
        },
        [deepLinkContext]
    );

    const setDeepLinkRuntimeStateSlice = useCallback<UseDeepLinkRuntimeStateReturn['setDeepLinkRuntimeState']>(
        (path, value, options) => {
            deepLinkContext.setDeepLinkRuntimeState(value, {
                ...options,
                path
            });
        },
        [deepLinkContext]
    );

    const clearDeepLinkRuntimeState = useCallback<UseDeepLinkRuntimeStateReturn['clearDeepLinkRuntimeState']>(
        (path: string) => {
            deepLinkContext.clearDeepLinkRuntimeState(path);
        },
        [deepLinkContext]
    );

    const setDeepLinkItemId = useCallback<UseDeepLinkRuntimeStateReturn['setDeepLinkItemId']>(
        (path: string, id: string) => {
            const isValidItemPath = isValidLinkedEntityNodeUIStatePath(path);

            if (!isValidItemPath) {
                console.warn(`Attempted to set item id on invalid deep link UI state path: ${path}`);
                return;
            }

            const currentItemValue = deepLinkContext.getDeepLinkRuntimeState<DeepLinkUIStateLinkedEntityNodeValue>({
                path
            });
            /**
             * The node corresponding to this item / linked entity path must exist.
             * This indicates that a UI view corresponding to the linked entity (e.g. the notes side panel) is active.
             * An item id can only be set for an active linked entity node in the UI state, to prevent accidentally
             * setting an item id for a non-active view and ending up activating it unintentionally.
             */
            if (!currentItemValue) {
                console.warn(
                    `Attempted to set item id on deep link UI state path ${path} that does not point to an existing value`
                );
                return;
            }

            deepLinkContext.setDeepLinkRuntimeState({ id }, { path });
        },
        [deepLinkContext]
    );

    const unsetDeepLinkItemId = useCallback<UseDeepLinkRuntimeStateReturn['unsetDeepLinkItemId']>(
        (path: string) => {
            const isValidItemPath = isValidLinkedEntityNodeUIStatePath(path);

            if (!isValidItemPath) {
                console.warn(`Attempted to unset item id on invalid deep link UI state path: ${path}`);
                return;
            }

            const currentItemValue = deepLinkContext.getDeepLinkRuntimeState<DeepLinkUIStateLinkedEntityNodeValue>({
                path
            });

            /**
             * The node corresponding to this item / linked entity path must exist.
             * This indicates that a UI view corresponding to the linked entity (e.g. the notes side panel) is active.
             * An item id can only be unset for an active linked entity node in the UI state, to prevent accidentally
             * unsetting an item id for a non-active view and ending up activating it unintentionally.
             */
            if (!currentItemValue) {
                console.warn(
                    `Attempted to unset item id on deep link UI state path ${path} that does not point to an existing value`
                );
                return;
            }

            /**
             * "Unsetting" means we replace the object value containing the item id with a boolean `true` value.
             * This is done to preserve any UI state that depends on this node having a truthy value, e.g. an open side panel.
             */
            deepLinkContext.setDeepLinkRuntimeState(true, { path });
        },
        [deepLinkContext]
    );

    return useMemo(
        () => ({
            getDeepLinkRuntimeState,
            setDeepLinkRuntimeState: setDeepLinkRuntimeStateSlice,
            clearDeepLinkRuntimeState,
            setDeepLinkItemId,
            unsetDeepLinkItemId
        }),
        [
            getDeepLinkRuntimeState,
            setDeepLinkRuntimeStateSlice,
            clearDeepLinkRuntimeState,
            setDeepLinkItemId,
            unsetDeepLinkItemId
        ]
    );
};
